<template>
    <div
        v-loading="!getUser || !getUser.userId || loading"
        class="user-portfolio-personal-data"
        :class="{
            'user-portfolio-personal-data--deleted': getUser.deletedAt,
        }"
    >
        <div v-if="getUser.deletedAt" class="user-portfolio-personal-data__deleted">
            Профиль удален
        </div>
        <div v-if="getUser.draft" class="user-portfolio-personal-data__draft"> Черновик </div>
        <div
            v-if="!getUser.deletedAt"
            class="user-portfolio-personal-data__row user-portfolio-personal-data__row--bookmark"
            @click="toggleBookmark"
        >
            <IconBookmark class="user-portfolio-personal-data__link" :active="getUserIsAFriend"  />
        </div>
        <div
            class="user-portfolio-personal-data__row user-portfolio-personal-data__row--image"
            :class="{ 'user-portfolio-personal-data__row--no-image': !image }"
        >
            <img v-if="image" :src="image" alt="" />
            <img v-else src="~@/assets/images/account/placeHolder-m.png" alt="" />

            <a
                v-if="hasUserContactsAccess"
                class="admin-link"
                :href="adminLink"
                target="_blank"
            >
                Смотреть подробную информацию
            </a>
        </div>
        <div class="user-portfolio-personal-data__row user-portfolio-personal-data__row--user-id">
            <span>User ID:</span>
            <span>{{ getUser.userId }}</span>
        </div>
        <div class="user-portfolio-personal-data__row user-portfolio-personal-data__row--name">
            <template v-if="!isMobile">
                <div v-for="(name, index) in fullNameArray" :key="index" v-html="name" />
            </template>
            <template v-else>
                <span v-html="fullNameArray.join(' ')" />
            </template>
        </div>
        <div class="user-portfolio-personal-data__row">
            <div>Возраст:</div>
            <div class="user-portfolio-personal-data__value">{{ age }}</div>
        </div>
        <div class="user-portfolio-personal-data__row">
            <div>Пол:</div>
            <div class="user-portfolio-personal-data__value">
                {{ getUser.gender !== null ? genders[getUser.gender ? 1 : 0] : 'Не указан' }}
            </div>
        </div>
        <div class="user-portfolio-personal-data__row">
            <div>Город проживания:</div>
            <div class="user-portfolio-personal-data__value" v-html="city" />
        </div>
        <div class="user-portfolio-personal-data__row">
            <div>О себе:</div>
            <div
                class="user-portfolio-personal-data__value"
                v-html="about"
            />
        </div>

        <PortfolioUserTags
            class="user-portfolio-personal-data__row"
        />
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {parseISO, differenceInYears} from "date-fns";
import str_counter from "@/tools/str_counter";
import IconBookmark from "@/components/UI/icons/IconBookmark.vue";
import PortfolioUserTags from "@/components/portfolio/tags/PortfolioUserTags.vue";
import {getHighlightGroupsKeywordsSet, highlightStrKeywords} from "@/tools/graphQueryHelpers";

export default {
    components: {
        PortfolioUserTags,
        IconBookmark,
    },

    props: {
        highlights: {
            type: Object,
            required: false,
            default: () => null,
        },
    },

    data: () => ({
        genders: { 1: "Мужской", 0: "Женский" },
        loading: false,
    }),

    computed: {
        ...mapGetters([
            'isMobile',
        ]),
        ...mapGetters('newUsers', [
            'getUser',
            'getUserIsAFriend',
        ]),
        ...mapGetters('auth', [
            'hasUserContactsAccess',
        ]),

        highlightKeywords() {
            return getHighlightGroupsKeywordsSet(this.highlights, [
                'profile.fullname',
                'profile.surname',
                'profile.name',
                'profile.patronymic',
                'profile.about',
                'profile.address.value',
            ]);
        },

        fullNameArray() {
            const user = this.getUser;

            const data = [
                user.surname ? highlightStrKeywords(user.surname, this.highlightKeywords) : null,
                user.name ? highlightStrKeywords(user.name, this.highlightKeywords) : null,
                user.patronymic ? highlightStrKeywords(user.patronymic, this.highlightKeywords) : null,
            ].filter(v => v);

            return data.length ? data : ["Имя не указано"];
        },

        about() {
            const about = this.getUser.about;

            return about
                ? highlightStrKeywords(about, this.highlightKeywords).replaceAll(/(\r\n|\n|\r)/g, '<br/>')
                : 'Нет данных'
            ;
        },

        age() {
            const dateOfBirth = this.getUser && this.getUser.dateOfBirth;
            const ageReady = this.getUser && this.getUser.age;

            if (!dateOfBirth) {
                return "Нет данных";
            }

            const age = differenceInYears(new Date(), parseISO(dateOfBirth));
            const postfix = str_counter('', 'год', 'года', 'лет')(age);

            return `${age} ${postfix}`;
        },

        city() {
            const address = this.getUser && this.getUser.address;

            if (!address || !address.value) {
                return "Нет данных";
            }

            return highlightStrKeywords(address.value, this.highlightKeywords);
        },

        image() {
            const avatar = this.getUser && this.getUser.avatar;
            const official = this.getUser && this.getUser.avatarOfficial;

            return (official && official.url) || (avatar && avatar.url);
        },

        adminLink() {
            const domain = window.rsvDomain;
            const id = this.getUser && this.getUser.userId;

            return `https://administrator.${domain}/platform/profiles/users/${id}`;
        },
    },
    methods: {
        ...mapActions('newUsers', [
            'addFriend',
            'removeFriend',
        ]),
        ...mapActions('data', [
            'loadRegistryItems',
        ]),

        async toggleBookmark() {
            this.loading = true;

            if (this.getUser && this.getUser.userId) {
                if (this.getUserIsAFriend) {
                    await this.removeFriend(this.getUser.userId);
                } else {
                    await this.addFriend(this.getUser.userId);
                }
            }

            this.loading = false;
        },
    },
    async created() {
        await this.loadRegistryItems({ type: 'marital_status' });
    },
};

</script>

<style scoped lang="scss">
@import "~@/styles/rem";
@import '~@/styles/variables';

.user-portfolio-personal-data {
    position: relative;

    span {
        display: inline-block;
        padding-right: mrem(15);

        @include on-desktop {
            padding-right: rem(15);
        }
    }

    &__draft {
      color: #98A0A9;
      background: #F8F8F8;
      font-weight: 500;
      padding: mrem(6) mrem(20);
      font-size: mrem(16);
      line-height: mrem(34);
      margin: mrem(-20) mrem(-20) mrem(20) mrem(-20);
      border-radius: mrem(16) mrem(16) 0 0;

      @include on-desktop {
          padding: rem(6) rem(20);
          font-size: rem(16);
          line-height: rem(34);
          margin: rem(-20) rem(-20) rem(20) rem(-20);
          border-radius: rem(16) rem(16) 0 0;
      }
    }

    &__deleted {
        color: $color-main-red;
        background: $color-main-error-lighter;
        font-weight: 500;
        padding: mrem(6) mrem(20);
        font-size: mrem(16);
        line-height: mrem(34);
        margin: mrem(-20) mrem(-20) mrem(20) mrem(-20);
        border-radius: mrem(16) mrem(16) 0 0;

        @include on-desktop {
            padding: rem(6) rem(20);
            font-size: rem(16);
            line-height: rem(34);
            margin: rem(-20) rem(-20) rem(20) rem(-20);
            border-radius: rem(16) rem(16) 0 0;
        }
    }

    &--deleted {
        .user-portfolio-personal-data__row--bookmark {
            top: rem(65);
        }
    }

    &__row {
        $currentClass: &;

        margin-bottom: mrem(16);

        @include on-desktop {
            margin-bottom: rem(16);
        }

        &:nth-last-child(1 of #{$currentClass}) {
            margin-bottom: 0;
        }

        &--user-id {
            color: $color-main-blue;
            font-weight: 600;
        }

        &--name {
            font-size: mrem(18);
            line-height: mrem(24);
            font-weight: 700;

            @include on-desktop {
                font-size: rem(18);
                line-height: rem(24);
            }
        }

        &--image {
            width: 100%;
            min-height: mrem(200);
            border-radius: mrem(16);
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            align-content: center;
            gap: mrem(16);

            @include on-desktop {
                float: right;
                width: rem(200);
                min-height: rem(200);
                margin-left: rem(35);
                margin-right: rem(46);
                border-radius: rem(16);
                gap: rem(16);
            }

            .admin-link {
                color: $color-secondary-grey;
                text-align: center;
                font-style: normal;
                font-weight: 500;
                font-size: mrem(16);
                line-height: mrem(22);

                &:hover {
                    text-decoration: underline !important;
                }

                @include on-desktop {
                    font-size: rem(16);
                    line-height: rem(22);
                }
            }

            img {
                width: 100%;
                object-fit: cover;
                border-radius: mrem(16);

                @include on-desktop {
                    border-radius: rem(16);
                }
            }
        }

        &--no-image {
            img {
                border: mrem(1) solid $color-main-notice-light;

                @include on-desktop {
                    border-size: rem(1);
                }
            }
        }

        &--bookmark {
            position: absolute;
            top: mrem(40);
            right: mrem(40);

            @include on-desktop {
                top: rem(20);
                right: rem(20);
            }
        }
    }

    &__value {
        font-weight: 600;
    }

    &__link {
        cursor: pointer;
    }
}
</style>
